import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { cryptoAssets as staticAssets, CryptoAsset, buildCryptoAsset } from '@/lib/crypto-data';
import { isSupportedCrypto } from '@/lib/supported-symbols';

interface CommissionTier {
  min_amount: number;
  max_amount: number | null;
  commission_rate: number;
}

interface UseCryptoPricesReturn {
  cryptoAssets: CryptoAsset[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refetch: () => Promise<void>;
  commissionTiers: CommissionTier[];
  getCommissionRate: (amount: number) => number;
  calculateSellPrice: (basePrice: number, amount: number) => number;
}

const DEFAULT_COMMISSION_RATE = 20;

export const useCryptoPrices = (): UseCryptoPricesReturn => {
  const [assets, setAssets] = useState<CryptoAsset[]>(staticAssets);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [commissionTiers, setCommissionTiers] = useState<CommissionTier[]>([]);

  const fetchPrices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fnError } = await supabase.functions.invoke('crypto-prices');
      
      if (fnError) {
        console.error('Error fetching prices:', fnError);
        throw fnError;
      }
      
      if (data?.success) {
        // New format: use assets array from API
        if (data.assets && Array.isArray(data.assets)) {
          const seen = new Set<string>();
          const dynamicAssets = data.assets
            .filter((a: any) => {
              const sym = (a.symbol || '').toUpperCase();
              if (seen.has(sym) || !isSupportedCrypto(sym)) return false;
              seen.add(sym);
              return true;
            })
            .map((a: any) => buildCryptoAsset({
              symbol: a.symbol,
              name: a.name,
              price: a.price,
              sellPrice: a.sellPrice,
              change24h: a.change24h,
              image: a.image,
              market_cap_rank: a.market_cap_rank,
            }));
          setAssets(dynamicAssets);
        }
        
        setLastUpdated(new Date(data.updatedAt));
        
        if (data.commissionTiers) {
          setCommissionTiers(data.commissionTiers);
        }
      } else if (data?.error) {
        throw new Error(data.error);
      }
    } catch (err) {
      console.error('Failed to fetch crypto prices:', err);
      setError('فشل في جلب الأسعار الحية');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, [fetchPrices]);

  const getCommissionRate = useCallback((amount: number): number => {
    if (commissionTiers.length === 0) return DEFAULT_COMMISSION_RATE;
    const matchingTier = commissionTiers.find((tier) => {
      const minMatch = amount >= tier.min_amount;
      const maxMatch = tier.max_amount === null || amount <= tier.max_amount;
      return minMatch && maxMatch;
    });
    return matchingTier?.commission_rate ?? DEFAULT_COMMISSION_RATE;
  }, [commissionTiers]);

  const calculateSellPrice = useCallback((basePrice: number, amount: number): number => {
    const rate = getCommissionRate(amount);
    return basePrice * (1 + rate / 100);
  }, [getCommissionRate]);

  return {
    cryptoAssets: assets,
    loading,
    error,
    lastUpdated,
    refetch: fetchPrices,
    commissionTiers,
    getCommissionRate,
    calculateSellPrice,
  };
};
